import React, {useState, useEffect, useRef, useMemo} from 'react';
import Moment from 'moment';
import {createRefetchContainer, graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import {PageWrapper} from './TimeApprovalPage_styled';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import * as tracking from '../../../tracking';
import {hideLoader, showLoader} from '../../../containers/global_loader';
import TimeApprovalSection from './TimeApprovalSection';
import HeaderBar from '../../../forecast-app/shared/components/headers/header-bar/header_bar';
import {injectIntl} from 'react-intl';
import {ELEMENT_TYPE, FILTER_TYPE, FILTER_SECTION, TIERS} from '../../../constants';
import {handleAdjacentPeriodClick, handleDateRangeClick, TIME_PERIOD} from '../../../forecast-app/my-work-tab/my-timesheets-page/timesheets_change_date_view';
import {getSocketConfig} from './TimeApprovalSocketHandling';
import {FILTER_SECTIONS} from '../../../forecast-app/shared/components/filters/FilterWrapper';
import {getFilterFunctions} from '../../../forecast-app/shared/components/filters/filter_logic';
import {getFiltersAlphabetically} from '../../../forecast-app/shared/components/filters/filter_util';
import Util from '../../../forecast-app/shared/util/util';
import {getInitialOptions, handleChangedOptions, theEyeToColumns} from '../../../the_eye_util';
import {theEyeOptions} from './TimeApprovalTheEye';
import {GROUP_BY} from './TimeApprovalLogic';
import {groupBy} from 'lodash';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';
import {getCompanyLockedDate} from '../time-lock/TimeLockUtil';

const TimeApprovalPage = ({intl, viewer, relay, setSocketConfig, setSocketVariables, history}) => {
	const localFilters = Util.localStorageGetItem('time-approval-filters') ? JSON.parse(Util.localStorageGetItem('time-approval-filters')) : null;

	const [currentViewingDate, setCurrentViewingDate] = useState(Moment().startOf('w'));
	const [filterFunctions, setFilterFunctions] = useState(localFilters ? getFilterFunctions(localFilters) : () => true);
	const localStorageTheEyeName = 'the-eye-time-approval';
	const [_theEyeOptions, setTheEyeOptions] = useState(getInitialOptions(theEyeOptions(), localStorageTheEyeName));
	const enabledColumns = useMemo(() => theEyeToColumns(_theEyeOptions), [_theEyeOptions]);
	const timePeriod = TIME_PERIOD.WEEK_VIEW;
	const didMount = useRef(false);

	const [_groupBy, setGroupBy] = useState(Util.localStorageGetItem('time-approval-group-by') || GROUP_BY.PERSON);

	const companyWeekLocked = useMemo(() => {
		const companyLockedDate = getCompanyLockedDate(viewer.company);
		const endOfWeek = currentViewingDate.clone().endOf('week');
		return companyLockedDate && companyLockedDate.isSameOrAfter(endOfWeek);
	}, [currentViewingDate]);

	useEffect(() => {
		document.title = 'Time Approval - Forecast';
		tracking.trackPage('Time-approval');
		setSocketConfig(getSocketConfig(), relay.refetch);
		dispatch(EVENT_ID.PAGE_LOADED, 'time-approval');
	}, []);

	const refetch = variables => {
		showLoader();
		relay.refetch(variables, null, () => hideLoader());
		setSocketVariables(variables);
	};

	useEffect(() => {
		// Only run if not mount
		if (!didMount.current) {
			didMount.current = true;
		} else {
			//we need to fetch now the time entries of each person
			const startDate = currentViewingDate.clone().startOf('week').format('YYYY-MM-DD');

			const endDate = currentViewingDate.clone().endOf('week').format('YYYY-MM-DD');

			refetch({
				startDateString: startDate,
				endDateString: endDate,
			});
		}
	}, [currentViewingDate]);

	const goToToday = () => {
		setCurrentViewingDate(Moment().startOf('w'));
	};

	const adjacentPeriod = forward => {
		setCurrentViewingDate(handleAdjacentPeriodClick(currentViewingDate, forward, timePeriod));
	};

	const handleDateRange = date => {
		setCurrentViewingDate(handleDateRangeClick(date, timePeriod));
	};

	const onFilterChange = (filters, filterFunctions) => {
		Util.localStorageSetItem(`time-approval-filters`, JSON.stringify(filters));
		setFilterFunctions(filterFunctions);
	};

	const handleTheEyeOptionSelect = (_, __, ___, newOptions) => {
		tracking.trackEyeSelector(newOptions);
		setTheEyeOptions(handleChangedOptions(newOptions, localStorageTheEyeName));
	};

	const handleGroupByChange = val => {
		Util.localStorageSetItem('time-approval-group-by', val);
		tracking.trackPageAction('Group by changed', {groupBy: val});
		setGroupBy(val);
	};

	const getHeader = () => {
		const leftContent = [];
		const rightContent = [];

		const dateChanger = {
			type: ELEMENT_TYPE.DATE_CHANGER_V2,
			useTodayButton: true,
			timePeriod: TIME_PERIOD.WEEK_VIEW,
			currentViewingDate: currentViewingDate,
			handleTodayButtonClick: goToToday.bind(this),
			handleAdjacentDateButtonClick: adjacentPeriod.bind(this),
			updateDateRange: handleDateRange.bind(this),
			boldLongDayMonth: true,
			selectorTooltipEnabled: true,
			todayButtonLeft: true,
			selectorTooltipProps: {
				autoPlace: true,
				grey: true,
				infoText: intl.formatMessage({id: 'common.select_date'}),
			},
			tooltipEnabled: true,
			tooltipProps: {
				autoPlace: true,
				infoText: Moment().format('ddd, DD. MMM') + ' ' + Moment().format('YYYY'),
				grey: true,
			},
		};
		leftContent.push(dateChanger);

		if (companyWeekLocked) {
			leftContent.push({
				type: ELEMENT_TYPE.HEADER_LABEL,
				text: intl.formatMessage({id: 'time_lock.week_locked'}),
			});
		}

		const groupingOptions = [
			{value: GROUP_BY.PERSON, dataCy: 'grouping-dropdown-option-person', label: intl.formatMessage({id: 'common.group_by_person'})},
			{value: GROUP_BY.PROJECT, dataCy: 'grouping-dropdown-option-project', label: intl.formatMessage({id: 'common.group_by_project'})},
		];

		const groupingDropdown = {
			type: ELEMENT_TYPE.DROPDOWN,
			dropdownOptions: groupingOptions,
			value: _groupBy,
			callback: group => handleGroupByChange(group.value),
			placeholder: intl.formatMessage({id: 'project_budget.select_period'}),
			userpilot: 'budget-period-dropdown',
			dataCy: 'grouping-dropdown',
		};
		rightContent.push(groupingDropdown);

		const peopleFilters = [FILTER_TYPE.TEAM_MEMBER, FILTER_TYPE.TEAM, FILTER_TYPE.ROLE];
		if ([TIERS.ENTERPRISE, TIERS.TRIAL].includes(viewer.company.tier)) {
			peopleFilters.push(FILTER_TYPE.DEPARTMENT);
		}
		const projectFilters = [FILTER_TYPE.PROJECT, FILTER_TYPE.PROJECT_TYPE, FILTER_TYPE.CLIENT];
		const taskFilters = [];
		const filter = {
			type: ELEMENT_TYPE.FILTER_V4,
			defaultSection: _groupBy === GROUP_BY.PROJECT ? FILTER_SECTIONS.PROJECTS : FILTER_SECTIONS.PEOPLE,
			projectFilters: getFiltersAlphabetically(projectFilters, intl.formatMessage),
			peopleFilters: getFiltersAlphabetically(peopleFilters, intl.formatMessage),
			taskFilters,
			viewer: viewer,
			filterSection: FILTER_SECTION.TIME_APPROVAL,
			appliedFiltersName: 'time-approval-filters',
			onFiltersChange: onFilterChange,
		};
		rightContent.push(filter);

		const theEye = {
			type: ELEMENT_TYPE.THE_EYE,
			options: _theEyeOptions,
			onSelect: handleTheEyeOptionSelect,
			expandLeft: true,
			userpilot: 'eye-selector',
		};
		rightContent.push(theEye);

		// if (viewer.availableFeatureFlags.some(flag => flag.key === 'third_level_nav')) {
		// 	return buildThirdLevelNavHeader(leftContent, rightContent, 'timesheet.time_approval');
		// }

		return <HeaderBar thirdLevelMargin leftContent={leftContent} rightContent={rightContent} useThirdLevelNav />;
	};

	const filterOptions = {teams: viewer.company.teams.edges};

	let timeRegs = viewer.company.timeRegistrations.edges.map(tReg => tReg.node);
	if (filterFunctions) {
		timeRegs = timeRegs.filter(
			tReg => (!filterFunctions.personFilter || filterFunctions.personFilter(tReg.person, filterOptions)) && (!filterFunctions.projectFilter || filterFunctions.projectFilter(tReg.project || tReg.task.project, filterOptions))
		);
	}
	const groupedTimeRegs = groupBy(timeRegs, 'approvalStatus');

	return (
		<PageWrapper>
			{getHeader()}
			<TimeApprovalSection groupedTimeRegs={groupedTimeRegs} currentViewingDate={currentViewingDate} intl={intl} enabledColumns={enabledColumns} group={_groupBy} actualPersonId={viewer.actualPersonId} />
		</PageWrapper>
	);
};

const TimeApprovalPageQuery = graphql`
	query TimeApprovalPage_Query($searchQuery: TaskSearchQueryType, $startDateString: String, $endDateString: String) {
		viewer {
			actualPersonId
			component(name: "time-approval")
			projectOwner
			...TimeApprovalPage_viewer @arguments(searchQuery: $searchQuery, startDateString: $startDateString, endDateString: $endDateString)
		}
	}
`;

export {TimeApprovalPageQuery};

export default withRouter(
	injectIntl(
		withSocketHandling(
			createRefetchContainer(
				TimeApprovalPage,
				{
					viewer: graphql`
						fragment TimeApprovalPage_viewer on Viewer @argumentDefinitions(searchQuery: {type: "TaskSearchQueryType"}, startDateString: {type: "String"}, endDateString: {type: "String"}) {
							component(name: "time-approval")
							id
							harvestUser
							actualPersonId
							email
							availableFeatureFlags {
								key
							}
							language
							filters(first: 10000, filterSection: TIME_APPROVAL) @connection(key: "Viewer_filters", filters: []) {
								edges {
									node {
										id
										name
										section
										value
										updatedAt
									}
								}
							}
							company {
								id
								harvestEnabled
								characterLimit
								lockedPeriodYear
								lockedPeriodMonth
								lockedPeriodDay
								tier
								modules {
									moduleType
								}
								timeRegistrations(first: 10000000, startDate: $startDateString, endDate: $endDateString, searchQuery: $searchQuery, approvableTime: true) @connection(key: "Company_timeRegistrations", filters: []) {
									edges {
										node {
											id
											approvalStatus
											noApprovalStatusChange
											day
											month
											year
											minutesRegistered
											notes
											person {
												id
												firstName
												lastName
												fullName
												profilePictureId
												department {
													id
												}
												role {
													id
												}
											}
											project {
												id
												name
												projectColor
												companyProjectId
												fullAccessToProject
												budgetType
												client {
													id
													name
												}
												projectPersons(first: 100, contactsOnly: true) {
													edges {
														node {
															person {
																id
																fullName
																profilePictureId
															}
														}
													}
												}
											}
											task {
												id
												name
												companyTaskId
												estimateForecast
												totalMinutesRegistered
												project {
													id
													name
													companyProjectId
													projectColor
													fullAccessToProject
													estimationUnit
													minutesPerEstimationPoint
													budgetType
													client {
														id
														name
													}
													projectPersons(first: 100, contactsOnly: true) {
														edges {
															node {
																person {
																	id
																	fullName
																	profilePictureId
																}
															}
														}
													}
												}
											}
										}
									}
								}
								teams(first: 1000000) @connection(key: "Company_teams", filters: []) {
									edges {
										node {
											id
											name
											teamPersons(first: 1000) @connection(key: "Team_teamPersons", filters: []) {
												edges {
													node {
														id
														person {
															id
														}
													}
												}
											}
										}
									}
								}
							}
						}
					`,
				},
				graphql`
					query TimeApprovalPageRefetchQuery($searchQuery: TaskSearchQueryType, $startDateString: String, $endDateString: String) {
						viewer {
							component(name: "time-approval")
							...TimeApprovalPage_viewer @arguments(searchQuery: $searchQuery, startDateString: $startDateString, endDateString: $endDateString)
						}
					}
				`
			)
		)
	)
);
